import { Button, Card, CardContent, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@1trading/react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CoinCell } from '@/components/ui/coin-cell';
import { StatusChip } from '@/components/ui/status-chip';
import { type ClientLoan, getClientEurosTotal } from '@/services/api/loans';
import { formatEuros } from '@/utils/format';
import { capitalize } from '@/utils/string';
import { AddAssetDialog } from './add-asset-dialog';
import { CreditLineDialog, type CreditLineDialogMode } from './credit-line-dialog';

const CURRENCY_ICON_SIZE = 32;

type CardDialogState = { kind: 'credit-line'; currency: string; mode: CreditLineDialogMode } | { kind: 'add-asset' } | null;

interface LoanCardProps {
  client: ClientLoan;
  /** Refetch loans after any mutation (full-state upsert, last-writer-wins) */
  onMutated: () => void;
  /** HTTP 403 → the page-level Unauthorised Action dialog */
  onUnauthorised: () => void;
}

// One full-width card per institution.
export const LoanCard = ({ client, onMutated, onUnauthorised }: LoanCardProps) => {
  const [dialog, setDialog] = useState<CardDialogState>(null);
  const currencyRows = useMemo(() => Object.entries(client.loans), [client.loans]);
  // Derived from raw numbers at render — never stored formatted
  const eurosTotal = useMemo(() => getClientEurosTotal(client), [client]);

  return (
    <Card className="w-full overflow-hidden py-0">
      <div className="flex flex-wrap items-center gap-3 border-b bg-muted/50 px-6 py-3">
        <StatusChip variant="success">{capitalize(client.clientName)}</StatusChip>
        <span className="text-sm text-muted-foreground">
          PID:{' '}
          <Link className="font-mono text-foreground underline-offset-2 hover:underline" to={`/operations/users/${client.accountId}`}>
            {client.accountId}
          </Link>
        </span>
      </div>

      <CardContent className="flex flex-col gap-4 pt-4 pb-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Currency</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">In Euro</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {currencyRows.length === 0 ? (
              <TableRow>
                <TableCell className="h-16 text-center text-muted-foreground" colSpan={4}>
                  No credit lines yet
                </TableCell>
              </TableRow>
            ) : null}
            {currencyRows.map(([currency, position]) => (
              <TableRow key={currency}>
                <TableCell>
                  {/* Fix: CoinCell hides the icon when the assets CDN has
                      no SVG for the code, instead of rendering a broken image */}
                  <CoinCell asset={currency} size={CURRENCY_ICON_SIZE} />
                </TableCell>
                <TableCell className="text-right tabular-nums">{position.quantity}</TableCell>
                {/* Kept: unpriced currencies arrive as inEuros 0 and render €0.00 */}
                <TableCell className="text-right tabular-nums">{formatEuros(position.inEuros)}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      aria-label={`Update ${currency} credit line`}
                      className="text-primary"
                      onClick={() => setDialog({ kind: 'credit-line', currency, mode: 'update' })}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      aria-label={`Delete ${currency} credit line`}
                      className="text-red-500"
                      onClick={() => setDialog({ kind: 'credit-line', currency, mode: 'delete' })}
                      size="icon"
                      type="button"
                      variant="ghost"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-lg font-semibold tabular-nums">{formatEuros(eurosTotal)} credit line total</h3>
          <Button className="gap-2" onClick={() => setDialog({ kind: 'add-asset' })} type="button">
            <Plus className="size-4" />
            Add New Asset
          </Button>
        </div>
      </CardContent>

      {dialog?.kind === 'credit-line' ? (
        <CreditLineDialog client={client} currency={dialog.currency} mode={dialog.mode} onClose={() => setDialog(null)} onMutated={onMutated} onUnauthorised={onUnauthorised} />
      ) : null}
      {dialog?.kind === 'add-asset' ? <AddAssetDialog client={client} onClose={() => setDialog(null)} onMutated={onMutated} onUnauthorised={onUnauthorised} /> : null}
    </Card>
  );
};
LoanCard.displayName = 'LoanCard';
